import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import React, { ReactNode } from 'react'

/**
 * Field configuration compatible with existing fieldsConfigSections pattern
 */
export interface LegacyFieldConfig {
  name: string
  label: string
  type?: 'text' | 'component'
  component?: React.ComponentType<any>
  componentProps?: Record<string, any>
  value?: any
  disabled?: boolean
  multiline?: boolean
  showAdornment?: boolean
  conditional?: boolean
  gridProps?: {
    xs?: number
    sm?: number
    md?: number
    lg?: number
    xl?: number
  }
}

/**
 * Section configuration compatible with existing fieldsConfigSections pattern
 */
export interface LegacySectionConfig {
  sectionLabel: string
  fields: LegacyFieldConfig[]
}

/**
 * Props for ProfileCardAdapter
 */
export interface ProfileCardAdapterProps {
  /** Array of sections from fieldsConfigSections */
  sections: LegacySectionConfig[]
  /** Whether in edit mode */
  isEditing: boolean
  /** Current entity data (organization, contact, user, etc.) */
  entity?: any
  /** @deprecated Use `entity` instead. Kept for backwards compatibility. */
  organization?: any
  /** Additional data for read-only rendering - passed through to ReadOnlyFieldComponent */
  renderContext?: Record<string, any>
  /** Function to get field value */
  getFieldValue: (fieldName: string) => any
  /** Change handler */
  onChange: (fieldName: string, newValue: any) => void
  /** Map of changed/dirty fields */
  changedFields?: Record<string, { newValue: any; isDirty: boolean }>
  /** Array of fields currently being saved */
  savingFields?: string[]
  /** Custom ReadOnlyField component */
  ReadOnlyFieldComponent?: React.ComponentType<any>
  /** Custom InfoLabel component */
  InfoLabelComponent?: React.ComponentType<{ children: ReactNode }>
  /** Whether to show special border styling (e.g., for parent organizations) */
  isParent?: boolean
  /** Custom styles */
  sx?: object
  /** Border style for parent entities */
  parentBorderColor?: string
  /** Use enhanced modern styling (default: true) */
  enhanced?: boolean
}

/**
 * Enhanced InfoLabel component - modern styling
 */
const EnhancedInfoLabel: React.FC<{ children: ReactNode }> = ({ children }) => {
  const theme = useTheme()
  return (
    <Typography
      sx={{
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  )
}

/**
 * Default InfoLabel component (legacy)
 */
const DefaultInfoLabel: React.FC<{ children: ReactNode }> = ({ children }) => {
  const theme = useTheme()
  return (
    <Typography
      sx={{
        color: theme.palette.text.secondary,
        fontWeight: 500,
        fontSize: '0.875rem',
      }}
    >
      {children}
    </Typography>
  )
}

/**
 * Enhanced Section Header component
 */
const EnhancedSectionHeader: React.FC<{ children: ReactNode }> = ({ children }) => {
  const theme = useTheme()
  return (
    <Box sx={{ mb: 2, mt: 1 }}>
      <Typography
        sx={{
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: '0.95rem',
          pb: 1,
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        {children}
      </Typography>
    </Box>
  )
}

/**
 * ProfileCardAdapter - Bridge component that adapts the existing fieldsConfigSections
 * pattern to work with a consistent profile card rendering approach.
 *
 * This allows gradual migration from OrganizationProfile's inline rendering
 * to a more declarative component-based approach.
 *
 * @example
 * ```tsx
 * const sections = fieldsConfigSections(organization, organizations, contacts, handleChange, ...)
 *
 * <ProfileCardAdapter
 *   sections={sections}
 *   isEditing={isEditing}
 *   organization={organization}
 *   getFieldValue={getFieldValue}
 *   onChange={handleChange}
 *   changedFields={changedFields}
 *   savingFields={savingFields}
 *   ReadOnlyFieldComponent={ReadOnlyField}
 *   renderContext={{ contacts, users, organizations, existingCustomProducts, isParent }}
 * />
 * ```
 */
const ProfileCardAdapter: React.FC<ProfileCardAdapterProps> = ({
  sections,
  isEditing,
  entity,
  organization, // deprecated - use entity instead
  renderContext = {},
  getFieldValue,
  onChange,
  changedFields = {},
  savingFields = [],
  ReadOnlyFieldComponent,
  InfoLabelComponent,
  isParent = false,
  sx,
  parentBorderColor,
  enhanced = true,
}) => {
  const theme = useTheme()
  // Support both `entity` and deprecated `organization` prop
  const currentEntity = entity ?? organization

  // Use enhanced or default styling based on prop
  const LabelComponent = InfoLabelComponent ?? (enhanced ? EnhancedInfoLabel : DefaultInfoLabel)

  return (
    <Box
      sx={{
        width: '100%',
        px: 3,
        py: 2,
        borderRadius: '20px',
        bgcolor: theme.palette.background.section,
        border: isParent ? `1px solid ${parentBorderColor ?? theme.palette.text.parent}` : undefined,
        ...sx,
      }}
    >
      {sections.map((section, sectionIndex) => (
        <Box key={section.sectionLabel} sx={{ mb: sectionIndex < sections.length - 1 ? 3 : 0 }}>
          {/* Section Header */}
          {enhanced ? (
            <EnhancedSectionHeader>{section.sectionLabel}</EnhancedSectionHeader>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                {section.sectionLabel}
              </Typography>
              <Box
                sx={{
                  height: '1px',
                  bgcolor: theme.palette.divider,
                  mx: 2,
                  my: 1,
                }}
              />
            </>
          )}

          {/* Fields Grid */}
          <Grid container spacing={enhanced ? 0.5 : 2}>
            {section.fields.map(field => {
              // Handle conditional rendering
              if (field.conditional === false) return null

              const fieldValue = field.value ?? getFieldValue(field.name)
              const isDirty = changedFields[field.name]?.isDirty

              return (
                <Grid key={field.name} item xs={12} sm={6} md={4} {...(field.gridProps || {})}>
                  {/* Enhanced field wrapper with hover effect */}
                  <Box
                    sx={
                      enhanced && !isEditing
                        ? {
                            py: 1,
                            px: 1.5,
                            borderRadius: 1.5,
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.03),
                            },
                          }
                        : {}
                    }
                  >
                    {isEditing ? (
                      <>
                        <LabelComponent>{field.label}</LabelComponent>
                        {field.type === 'component' && field.component ? (
                          <field.component
                            value={fieldValue}
                            onChange={onChange}
                            name={field.name}
                            users={renderContext.users}
                            isDirty={isDirty}
                            savingFields={savingFields}
                            {...(field.componentProps || {})}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: isDirty ? theme.palette.text.dirty : theme.palette.background.input,
                              },
                            }}
                          />
                        ) : (
                          <TextField
                            name={field.name}
                            value={fieldValue}
                            onChange={e => onChange(field.name, e.target.value)}
                            disabled={field.disabled}
                            multiline={field.multiline}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: isDirty ? theme.palette.text.dirty : theme.palette.background.input,
                                borderRadius: 1,
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: alpha(theme.palette.divider, 0.5),
                              },
                            }}
                            InputProps={{
                              endAdornment: savingFields.includes(field.name) ? (
                                <InputAdornment position="end">
                                  <CircularProgress size={16} thickness={5} />
                                </InputAdornment>
                              ) : undefined,
                            }}
                          />
                        )}
                      </>
                    ) : ReadOnlyFieldComponent ? (
                      <ReadOnlyFieldComponent
                        label={field.label}
                        name={field.name}
                        value={fieldValue}
                        entity={currentEntity}
                        renderContext={renderContext}
                        enhanced={enhanced}
                        // Legacy props for backwards compatibility with existing ReadOnlyField components
                        contacts={renderContext.contacts}
                        organizations={renderContext.organizations}
                        users={renderContext.users}
                        organization={currentEntity}
                        isParent={renderContext.isParent}
                        existingCustomProducts={renderContext.existingCustomProducts}
                      />
                    ) : (
                      <Box>
                        <LabelComponent>{field.label}</LabelComponent>
                        <Typography
                          sx={{
                            color: fieldValue ? theme.palette.text.primary : alpha(theme.palette.text.secondary, 0.5),
                            fontWeight: fieldValue ? 500 : 400,
                            fontSize: '0.95rem',
                            fontStyle: fieldValue ? 'normal' : 'italic',
                          }}
                        >
                          {Array.isArray(fieldValue)
                            ? fieldValue.length > 0
                              ? fieldValue.join(', ')
                              : 'Not specified'
                            : fieldValue || 'Not specified'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  )
}

export default ProfileCardAdapter
